import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Bike, X, Loader } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const T = {
  it: {
    title: 'Trova ricambi per la tua moto',
    subtitle: 'Marca → Anno → Modello',
    brand: 'Marca',
    year: 'Anno',
    model: 'Modello',
    selectBrand: 'Seleziona marca',
    selectYear: 'Seleziona anno',
    selectModel: 'Seleziona modello',
    reset: 'Reimposta',
    search: 'Cerca ricambi',
    noResults: 'Nessun prodotto compatibile',
    loading: 'Caricamento…',
  },
  en: {
    title: 'Find parts for your bike',
    subtitle: 'Brand → Year → Model',
    brand: 'Brand',
    year: 'Year',
    model: 'Model',
    selectBrand: 'Select brand',
    selectYear: 'Select year',
    selectModel: 'Select model',
    reset: 'Reset',
    search: 'Find parts',
    noResults: 'No compatible products',
    loading: 'Loading…',
  },
  es: {
    title: 'Encuentra recambios para tu moto',
    subtitle: 'Marca → Año → Modelo',
    brand: 'Marca',
    year: 'Año',
    model: 'Modelo',
    selectBrand: 'Selecciona marca',
    selectYear: 'Selecciona año',
    selectModel: 'Selecciona modelo',
    reset: 'Restablecer',
    search: 'Buscar recambios',
    noResults: 'Ningún producto compatible',
    loading: 'Cargando…',
  },
};

/**
 * BikeFilter — three-step cascading filter: brand → year → model
 *
 * Props:
 *   value: { brand, year, model }
 *   onChange(nextValue): called whenever any field changes
 *   onApply?(value): optional callback when the user clicks the big search button
 *   variant: 'inline' (compact, for /products) | 'hero' (large, for landing)
 */
export default function BikeFilter({ value, onChange, onApply, variant = 'inline' }) {
  const { language } = useLanguage();
  const t = T[language] || T.it;

  const [brands, setBrands] = useState([]);
  const [years, setYears] = useState([]);
  const [models, setModels] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [loadingYears, setLoadingYears] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);

  const brand = value?.brand || '';
  const year = value?.year || '';
  const model = value?.model || '';

  // Load brands once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/bikes/brands`);
        if (!cancelled && Array.isArray(r.data)) setBrands(r.data);
      } catch (_) {}
      if (!cancelled) setLoadingBrands(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Load years whenever brand changes
  useEffect(() => {
    if (!brand) { setYears([]); return; }
    let cancelled = false;
    setLoadingYears(true);
    (async () => {
      try {
        const r = await axios.get(`${API}/api/bikes/years`, { params: { brand } });
        if (!cancelled) setYears(r.data?.years || []);
      } catch (_) {}
      if (!cancelled) setLoadingYears(false);
    })();
    return () => { cancelled = true; };
  }, [brand]);

  // Load models whenever brand+year change
  useEffect(() => {
    if (!brand || !year) { setModels([]); return; }
    let cancelled = false;
    setLoadingModels(true);
    (async () => {
      try {
        const r = await axios.get(`${API}/api/bikes/models`, {
          params: { brand, year },
        });
        if (!cancelled) setModels(r.data?.models || []);
      } catch (_) {}
      if (!cancelled) setLoadingModels(false);
    })();
    return () => { cancelled = true; };
  }, [brand, year]);

  const update = useCallback((patch) => {
    onChange({ brand, year, model, ...patch });
  }, [brand, year, model, onChange]);

  const reset = () => onChange({ brand: '', year: '', model: '' });

  const isHero = variant === 'hero';

  return (
    <div
      className={`rounded-xl border transition-all ${
        isHero
          ? 'p-6 md:p-8 bg-gradient-to-br from-black/60 via-black/40 to-black/60 border-primary/30 shadow-xl shadow-primary/10'
          : 'p-4 bg-white/5 border-white/10'
      }`}
      data-testid="bike-filter"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className={`${isHero ? 'p-2 bg-primary/20 rounded-lg' : ''}`}>
          <Bike size={isHero ? 28 : 20} className="text-primary" />
        </div>
        <div>
          <h3 className={`font-bold ${isHero ? 'text-xl md:text-2xl' : 'text-base'}`}>
            {t.title}
          </h3>
          {isHero && (
            <p className="text-sm text-white/60 mt-1">{t.subtitle}</p>
          )}
        </div>
        {(brand || year || model) && (
          <button
            type="button"
            onClick={reset}
            className="ml-auto flex items-center gap-1 text-xs text-white/60 hover:text-primary transition-colors"
            data-testid="bike-filter-reset"
          >
            <X size={14} />
            {t.reset}
          </button>
        )}
      </div>

      <div className={`grid gap-3 ${isHero ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 sm:grid-cols-3'}`}>
        <select
          value={brand}
          onChange={(e) => update({ brand: e.target.value, year: '', model: '' })}
          disabled={loadingBrands}
          className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2.5 text-sm focus:border-primary focus:outline-none disabled:opacity-50"
          data-testid="bike-filter-brand"
          aria-label={t.brand}
        >
          <option value="">{loadingBrands ? t.loading : t.selectBrand}</option>
          {brands.map((b) => (
            <option key={b.brand} value={b.brand}>{b.brand} ({b.count})</option>
          ))}
        </select>

        <select
          value={year}
          onChange={(e) => update({ year: e.target.value, model: '' })}
          disabled={!brand || loadingYears}
          className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2.5 text-sm focus:border-primary focus:outline-none disabled:opacity-40"
          data-testid="bike-filter-year"
          aria-label={t.year}
        >
          <option value="">{loadingYears ? t.loading : t.selectYear}</option>
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>

        <select
          value={model}
          onChange={(e) => update({ model: e.target.value })}
          disabled={!brand || !year || loadingModels}
          className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2.5 text-sm focus:border-primary focus:outline-none disabled:opacity-40"
          data-testid="bike-filter-model"
          aria-label={t.model}
        >
          <option value="">{loadingModels ? t.loading : t.selectModel}</option>
          {models.map((m) => (
            <option key={m.model} value={m.model}>{m.model} ({m.count})</option>
          ))}
        </select>
      </div>

      {isHero && onApply && (
        <button
          type="button"
          onClick={() => onApply({ brand, year, model })}
          disabled={!brand}
          className="mt-5 w-full bg-primary hover:bg-primary/90 disabled:bg-primary/30 disabled:cursor-not-allowed text-black font-bold py-3 px-6 rounded-lg transition-all transform hover:scale-[1.01]"
          data-testid="bike-filter-apply"
        >
          {t.search}
        </button>
      )}
    </div>
  );
}
